import React, { useCallback, useState } from 'react'

import { bool, string } from 'prop-types'

import { AUTH_MODAL_EVENT, MODE } from 'Pods/LazyLoaded/AiSignInModal/constants'
import usePageConfig from 'Pods/Shared/Hooks/usePageConfig'
import { buildSubscriptionHref, STUDIO_RETURN_URL } from 'Pods/ThreeDStudio/Components/UsageModal/constants'

import { NOOP } from 'Utils/stableDefaults'

import HeroIntro from './Components/HeroIntro/HeroIntro'
import SubscriptionCard from './Components/SubscriptionCard/SubscriptionCard'
import WaitlistModal from './Components/WaitlistModal/WaitlistModal'

// The SubscriptionCard layouts (see SubscriptionCard). Every layout shows the subscription;
// the flags add the optional CTAs on top of it.
const LAYOUTS = {
  // Subscription + "Go to CGTrader AI" for users who can already generate.
  studioLink: { showStudioButton: true },
  // Subscription + free-trial block (with the struck-through price) + "Try Free Generations".
  freeTrial: { showTrialBlock: true, showOriginalPrice: true },
  // Subscription + free-trial block + "Join the Waitlist" / "I have access code" (opens the modal).
  joinWaitlist: { showTrialBlock: true, showWaitlistCta: true },
  // Subscription only.
  subscriptionOnly: {},
}

// Each Generator::SubscriptionState#tier maps to one layout. The 'subscribed' tier renders no
// header at all -- the page opens straight into the gallery and the bottom CTABanner carries
// "Start Creating with AI".
const TIER_LAYOUT = {
  in_free_beta: LAYOUTS.studioLink,
  has_credits: LAYOUTS.studioLink,
  must_join_waitlist: LAYOUTS.joinWaitlist,
  eligible_for_trial: LAYOUTS.freeTrial,
  not_subscribed: LAYOUTS.subscriptionOnly,
  // TODO: waitlisted needs its waitlist-specific flow (buying a subscription skips the waitlist).
  waitlisted: LAYOUTS.subscriptionOnly,
}

// Subscriber state is not available at SSR on this cacheable fast page, so the header mounts
// client-side only once pageConfig reports the per-user tier (see has_fast_application_state_helpers.rb).
// extractingCoreStyles (true only during the critical-CSS extraction pass) force-renders every
// variant so their styles are captured.
function UpgradeHeader({ extractingCoreStyles = false, subscriptionPaymentFormUrl = '' }) {
  const pageConfig = usePageConfig({ listener: 'full-page-config-loaded', checkIfAvailable: false })
  const [isWaitlistOpen, setIsWaitlistOpen] = useState(false)
  const [hasJoined, setHasJoined] = useState(false)

  const subscriptionHref = buildSubscriptionHref(
    pageConfig?.threeDStudioSubscriptionPaymentFormUrl || subscriptionPaymentFormUrl
  )

  const handleOpenWaitlist = useCallback(() => {
    setIsWaitlistOpen(true)
  }, [])

  const handleCloseWaitlist = useCallback(() => {
    setIsWaitlistOpen(false)
  }, [])

  const handleJoined = useCallback(() => {
    setHasJoined(true)
  }, [])

  // Signed-out "Try Free Generations" cannot grant the trial directly; hand off to the in-page
  // AI sign-in modal (signup) via the global event bus, mirroring the header's Log in / Sign up
  // buttons. useAuthModalTrigger (at the AiSubLanding root) opens the modal; post-auth lands on /ai.
  const handleTryFreeGenerations = useCallback(() => {
    if (typeof App === 'undefined' || !App.events) return
    App.events.publish(AUTH_MODAL_EVENT, { mode: MODE.SIGNUP })
  }, [])

  let cards
  let waitlistModal = null

  if (extractingCoreStyles) {
    // Render every layout so the critical-CSS extraction pass captures all of their styles.
    cards = Object.entries(LAYOUTS).map(([name, layout]) => (
      <SubscriptionCard
        key={name}
        {...layout}
        subscriptionHref={subscriptionHref}
        studioHref={STUDIO_RETURN_URL}
        onJoinWaitlist={NOOP}
      />
    ))
  } else {
    // Wait for the per-user payload before deciding. Rendering a default header pre-hydration
    // would flash for subscribers (who get none) and shift layout; SSR omits it for the same reason.
    if (!pageConfig) {
      return null
    }

    const tier = pageConfig.threeDStudioAccessTier

    // Subscribers: no header.
    if (tier === 'subscribed') {
      return null
    }

    // Signed-out visitors ('none') get no tier in pageConfig. Show the free-trial block when the
    // (class-level) trial pool has room, else subscription only; the trial CTA routes through
    // sign-in since the trial can only be granted once an account exists.
    const isSignedOut = !tier
    const signedOutLayout = pageConfig.threeDStudioFreeTrialAvailable
      ? LAYOUTS.freeTrial
      : LAYOUTS.subscriptionOnly
    const baseLayout = TIER_LAYOUT[tier] || signedOutLayout

    // Once the user joins the waitlist they are server-side `waitlisted`; mirror that tier's layout
    // (subscription only) so the hero card matches a fresh reload instead of still inviting a join.
    const layout = hasJoined ? LAYOUTS.subscriptionOnly : baseLayout

    cards = (
      <SubscriptionCard
        {...layout}
        subscriptionHref={subscriptionHref}
        studioHref={STUDIO_RETURN_URL}
        onJoinWaitlist={handleOpenWaitlist}
        onTryFreeGenerations={isSignedOut ? handleTryFreeGenerations : undefined}
      />
    )

    // must_join_waitlist users open the free-beta waitlist modal from the card CTAs. Mount it off
    // the base tier, not the post-join layout, so joining (which switches the card above) does not
    // unmount the modal mid-flow and drop its confirmation view.
    if (baseLayout.showWaitlistCta) {
      waitlistModal = (
        <WaitlistModal
          isOpen={isWaitlistOpen}
          isJoined={hasJoined}
          onClose={handleCloseWaitlist}
          onJoined={handleJoined}
          subscriptionHref={subscriptionHref}
        />
      )
    }
  }

  return (
    <section className="ai-sub-landing__upgrade-header">
      <div className="ai-sub-landing__upgrade-header-inner">
        <HeroIntro />
        {cards}
      </div>
      {waitlistModal}
    </section>
  )
}

UpgradeHeader.propTypes = {
  extractingCoreStyles: bool,
  subscriptionPaymentFormUrl: string,
}

export default UpgradeHeader
